import { useState } from 'react';
import { Pagination } from 'antd';

import { useLetterhead } from '@/api/bookings';
import type { TaxDocumentKind } from '@/api/generated/enums';
import {
  useInvoiceReadiness,
  useTaxDocuments,
  useTaxRegisterSummary,
  type TaxDocument,
  type TaxDocumentFilters,
} from '@/api/taxDocuments';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { DatePicker } from '@/components/DatePicker';
import { hueClasses } from '@/components/hues';
import { Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { useDebounced } from '@/components/useDebounced';
import { formatDocumentDate, toDayjs, toIsoDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import { can } from '@core/session/capability';
import type { Session } from '@core/session/types';
import { SheetPrint } from './print/SheetPrint';
import { invoiceSheet } from '@core/print/sheetHtml';
import { TaxDocumentDrawer } from './invoices/TaxDocumentDrawer';
import { readinessWarning } from './invoices/readiness';
import { documentTitle, kindLabel, kindTone } from './invoices/register';

const PAGE_SIZE = 25;

/**
 * The kind filter, as chips over the register.
 *
 * "Notes" is deliberately not one option: a credit note and a debit note run in opposite directions
 * and a shop reconciling a month reads them separately, however much GSTR-1 files them in one table.
 */
const KINDS: { value: TaxDocumentKind | undefined; label: string }[] = [
  { value: undefined, label: 'All' },
  { value: 'Invoice', label: 'Invoices' },
  { value: 'CreditNote', label: 'Credit notes' },
  { value: 'DebitNote', label: 'Debit notes' },
];

/**
 * Section 25's register — every tax invoice, credit note and debit note the shop has raised.
 *
 * <p>
 * <strong>Read-only, and there is no "new invoice" button.</strong> A statutory document comes out
 * of the event that made the supply: issuing garments raises the invoice, settling a deposit raises
 * the note. A button here would be a way to produce a document for a supply that did not happen,
 * which is a worse thing to have than a missing one.
 * </p>
 *
 * <p>
 * <strong>The totals are their own read rather than a sum of the page.</strong> A register is paged,
 * and a figure computed from twenty-five visible rows would be quoted as the month's — which is
 * exactly what a filing is made of. They are also behind `SalesInvoice:ViewFinancials`, which the
 * counter role does not hold: a clerk may well need to find a customer's invoice, and does not need
 * the shop's turnover on the same screen.
 * </p>
 */
export function InvoicesPage({ session }: { session: Session }) {
  const [kind, setKind] = useState<TaxDocumentKind | undefined>(undefined);
  const [from, setFrom] = useState<string | undefined>(undefined);
  const [to, setTo] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<TaxDocument | null>(null);
  const [printing, setPrinting] = useState<TaxDocument | null>(null);

  const debouncedSearch = useDebounced(search);

  const filters: TaxDocumentFilters = {
    kind,
    from,
    to,
    search: debouncedSearch || undefined,
    page,
    pageSize: PAGE_SIZE,
  };

  const branchId = session.branch?.id;

  const documents = useTaxDocuments(branchId, filters);

  // Fetched with the register rather than on the way to the sheet: the shop's own name and GSTIN
  // change about once a year, the query is cached for an hour, and a print that waits on a round
  // trip is a print a clerk presses twice.
  const letterhead = useLetterhead(branchId);

  // The pre-flight check — see `readiness.ts` for why it usually says nothing.
  const readiness = useInvoiceReadiness(branchId);
  const warning = readiness.data ? readinessWarning(readiness.data) : null;

  const maySeeTotals = can(session, 'SalesInvoice:ViewFinancials');
  const summary = useTaxRegisterSummary(branchId, filters, maySeeTotals);

  const total = documents.data?.total ?? 0;
  const totals = summary.data;

  /*
    A row of equal tiles takes one hue, which is the rule `TodayPage` follows: with a colour each,
    the eye stops reading four comparable figures and starts reading four unrelated cards. The hue
    is `invoices`' own, from `hues.ts`.
  */
  const tile = hueClasses.success.tile;

  const columns: DataViewColumn<TaxDocument>[] = [
    {
      title: 'Document',
      dataIndex: 'documentNumber',
      primary: true,
      render: (_, row) => (
        <div>
          <div className="font-mono-nums text-sm font-medium text-gray-900">
            {row.documentNumber}
          </div>
          <div className="text-[13px] text-default">
            {documentTitle(row)} · {formatDocumentDate(row.documentDate)}
          </div>
        </div>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      render: (_, row) => (
        <div>
          <div className="text-[13px] text-dark">{row.customerName}</div>
          <div className="font-mono-nums text-[11px] text-default">
            {row.bookingNumber || '—'}
            {row.customerGstin ? ` · ${row.customerGstin}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Kind',
      dataIndex: 'kind',
      render: (_, row) =>
        row.status === 'Cancelled' ? (
          <StatusBadge tone="neutral">Cancelled</StatusBadge>
        ) : (
          <StatusBadge tone={kindTone(row.kind)}>{kindLabel(row.kind)}</StatusBadge>
        ),
    },
    {
      title: 'Supply',
      dataIndex: 'placeOfSupply',
      hideOnCard: true,
      render: (_, row) => (
        <span className="font-mono-nums text-[13px]">
          {/* The place of supply and the split it produced, together — they are one fact read two
              ways, and a reader checking a return wants both without opening the sheet. */}
          {row.isBillOfSupply
            ? '—'
            : `${row.placeOfSupply ?? '—'} · ${row.isInterState ? 'IGST' : 'CGST+SGST'}`}
        </span>
      ),
    },
    {
      title: 'Taxable',
      dataIndex: 'taxableValue',
      align: 'right',
      render: (_, row) => (
        <span className="font-mono-nums text-[13px]">{formatAmount(row.taxableValue)}</span>
      ),
    },
    {
      title: 'Tax',
      dataIndex: 'igstAmount',
      align: 'right',
      render: (_, row) => (
        <span className="font-mono-nums text-[13px]">
          {formatAmount(row.cgstAmount + row.sgstAmount + row.igstAmount)}
        </span>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'documentTotal',
      align: 'right',
      render: (_, row) => (
        <span
          className={`font-mono-nums text-[13px] font-semibold ${
            row.status === 'Cancelled' ? 'text-default line-through' : 'text-gray-900'
          }`}
        >
          {formatAmount(row.documentTotal)}
        </span>
      ),
    },
    {
      title: '',
      dataIndex: 'id',
      trailing: true,
      render: (_, row) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            className="cursor-pointer rounded border border-border-color px-2.5 py-1 text-[13px] text-default hover:text-dark"
            onClick={() => setOpen(row)}
          >
            Open
          </button>

          {/* Print is on the row as well as inside the panel, because handing a customer their
              invoice is the errand somebody comes to this screen for — making them open the
              document first to find the button is a click that answers nothing. */}
          <button
            type="button"
            className="cursor-pointer rounded border border-border-color px-2.5 py-1 text-[13px] text-default hover:text-dark"
            onClick={() => setPrinting(row)}
          >
            Print
          </button>
        </div>
      ),
    },
  ];

  /*
    The sheet takes over the screen rather than opening in the drawer, which is what every other
    printed document here does — the register behind it would print too, and `#printableCard` is
    positioned at the page origin for exactly one article.

    A4 only: a tax invoice is a filed document rather than counter paper, so the roll and half-sheet
    sizes the customer copy offers do not apply. `PrintFrame` still gets its own `remember` key, on
    the rule that one machine has a habit per document.
  */
  if (printing && letterhead.data) {
    return (
      <SheetPrint
        eyebrow={printing.customerName}
        title={`${documentTitle(printing)} ${printing.documentNumber}`}
        remember="invoice"
        build={(paper, rollLength) =>
          invoiceSheet(
            {
              document: printing,
              letterhead: letterhead.data,
              printedAt: new Date().toISOString(),
            },
            paper,
            rollLength,
          )
        }
        onClose={() => setPrinting(null)}
      />
    );
  }

  return (
    <>
      <PageHeading
        title="Rental invoices"
        subtitle="Every document the shop has raised against a rental. Nothing is created here — an invoice comes out of issuing garments, and a note out of settling a deposit."
      />

      {/*
        Before the figures, because it is about whether the shop can trade at all rather than about
        what it has traded — and because a person who opens this screen to a broken setup should not
        have to scroll past four tiles of turnover to find out.
      */}
      {warning && (
        <div className="mb-3 lg:mb-6">
          <Notice tone={warning.tone} title={warning.title}>
            {warning.detail}
          </Notice>
        </div>
      )}

      {maySeeTotals && (
        <div className="mb-3 grid gap-3 sm:grid-cols-2 lg:mb-6 xl:grid-cols-4">
          <Stat
            label="Taxable value"
            value={formatAmount(totals?.taxableValue ?? 0)}
            hint={`${totals?.invoices ?? 0} invoice${(totals?.invoices ?? 0) === 1 ? '' : 's'}`}
            icon="icon icon-file-text"
            tile={tile}
          />
          <Stat
            label="CGST + SGST"
            value={formatAmount((totals?.cgstAmount ?? 0) + (totals?.sgstAmount ?? 0))}
            hint="supplied within the state"
            icon="icon icon-map-pin"
            tile={tile}
          />
          <Stat
            label="IGST"
            value={formatAmount(totals?.igstAmount ?? 0)}
            hint="supplied across a state line"
            icon="icon icon-truck"
            tile={tile}
          />
          <Stat
            label="Charged"
            value={formatAmount(totals?.documentTotal ?? 0)}
            /* Said out loud because it is the one figure here a credit note *reduces*, and a reader
               adding the four tiles together would otherwise double-count the notes. */
            hint={`net of ${totals?.creditNotes ?? 0} credit note${(totals?.creditNotes ?? 0) === 1 ? '' : 's'}`}
            icon="icon icon-indian-rupee"
            tile={tile}
          />
        </div>
      )}

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        <div className="mb-4 flex flex-wrap items-center gap-3 max-sm:mb-3 max-sm:gap-2">
          <div className="flex flex-wrap gap-1">
            {KINDS.map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => {
                  setKind(option.value);
                  setPage(1);
                }}
                aria-pressed={kind === option.value}
                className={`rounded border px-2.5 py-1 text-[13px] ${
                  kind === option.value
                    ? 'border-primary bg-primary text-white'
                    : 'border-border-color text-default hover:text-dark'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <DatePicker
              value={toDayjs(from) ?? null}
              onChange={(value) => {
                setFrom(toIsoDate(value));
                setPage(1);
              }}
              placeholder="From"
              allowClear
            />
            <DatePicker
              value={toDayjs(to) ?? null}
              onChange={(value) => {
                setTo(toIsoDate(value));
                setPage(1);
              }}
              placeholder="To"
              allowClear
            />
          </div>

          <input
            className="min-w-56 flex-1 rounded-md border border-border-color bg-white px-3 py-1.5 text-[13px]"
            placeholder="Document number or customer"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </div>

        <DataView<TaxDocument>
          columns={columns}
          dataSource={documents.data?.items ?? []}
          loading={documents.isLoading}
          empty={
            <EmptyState
              icon="icon icon-file-text"
              title={
                debouncedSearch || from || to || kind
                  ? 'Nothing matches those filters'
                  : 'No invoice has been raised yet'
              }
              hint={
                debouncedSearch || from || to || kind
                  ? 'Clear the search, the dates or the kind.'
                  : 'A tax invoice is raised the moment garments are issued to a customer, and a note when a deposit is settled with something to charge.'
              }
            />
          }
        />

        {total > PAGE_SIZE && (
          <div className="flex justify-end border-t border-border-color py-3">
            <Pagination
              current={page}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              showSizeChanger={false}
              showTotal={(count) => `${count} documents`}
            />
          </div>
        )}
      </Card>

      {open && (
        <TaxDocumentDrawer
          document={open}
          onClose={() => setOpen(null)}
          /* The panel closes as the sheet opens. Leaving it mounted would put a `fixed inset-0`
             overlay in the print, and `#printableCard` is positioned at the page origin for exactly
             one article. */
          onPrint={() => {
            setPrinting(open);
            setOpen(null);
          }}
        />
      )}
    </>
  );
}

/**
 * The template's stat card, from `salesDashboard.tsx`: label and figure on the left, a filled tile
 * under a white glyph on the right.
 *
 * <p>
 * Deliberately not `dashboard.tsx`'s, which stacks the tile above the label — reading down a row of
 * four, the eye wants the labels and the figures on one baseline so the numbers compare.
 * </p>
 */
function Stat({
  label,
  value,
  hint,
  icon,
  tile,
}: {
  label: string;
  value: string;
  hint: string;
  icon: string;
  tile: string;
}) {
  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-default mb-1 truncate text-[length:var(--scale-list-name)]">{label}</p>

          <h3 className="font-mono-nums mb-0 text-2xl font-bold text-gray-900 max-lg:text-xl">
            {value}
          </h3>

          <p className="text-default mt-0.5 mb-0 truncate text-[length:var(--scale-list-meta)]">
            {hint}
          </p>
        </div>

        <span
          className={`flex size-10 shrink-0 items-center justify-center rounded-md ${tile}`}
          aria-hidden="true"
        >
          <i className={`${icon} text-lg text-white`} />
        </span>
      </div>
    </Card>
  );
}
